import IDs from "@constants/componentIds";
import { BANK_STATUS } from "@constants/bankConstants";
import { WEBSITE_APPROVAL_STATUS, INTEGRATION_TYPE } from "@constants/websiteDetailsConstants";
import { MERCHANT_DOC_STATUS } from "@constants/manualUploadConstants";
import { getSkippedSteps } from "merchant-status-timeline";

// merchant-statuses
export const getProductAccountStatuses = (productAccountDetails) => {
  const isReKYC = productAccountDetails?.re_kyc_required;
  const productAccountStatuses = productAccountDetails?.product_account_statuses || [];
  const AgreementDetails = productAccountStatuses.find(v => v.status_type === 'Agreement') || {};
  const attachedConfigs = productAccountDetails?.attached_configs || [];
  const skippedStepsList = getSkippedSteps({ attachedConfigs }) || [];
  const isChildAggregator = productAccountDetails?.merchant_type == 'child_aggregator'
  const isPartnerAccount = productAccountDetails?.partner_uuid
  return ({
    pan: {
      filled: productAccountDetails?.pancard_number,
      verified: productAccountDetails?.pan_verification_status === BANK_STATUS.VERIFIED,
    },
    businessDetails: {
      completed: (
        productAccountDetails?.business_category_id &&
        productAccountDetails?.business_sub_category_id &&
        productAccountDetails?.monthly_expected_volume
      ),
    },
    bankDetails: {
      verified: productAccountDetails?.bank_detail?.bank_verification_status == BANK_STATUS.VERIFIED,
      filled: productAccountDetails?.bank_detail,
      uploadedProof: productAccountDetails?.bank_detail?.bank_verification_proof,
    },
    website: {
      completed: (
        productAccountDetails?.integration_type === INTEGRATION_TYPE.TOOLS ||
        productAccountDetails?.website_detail?.website_approval_status === WEBSITE_APPROVAL_STATUS.OK
      ),
    },
    account: {
      created: productAccountDetails?.account_uuid,
    },
    signingAuthority: {
      completed: productAccountDetails?.signatory_contact_details?.length || skippedStepsList.indexOf(IDs.signingAuthority.default) >= 0,
    },
    kyc: {
      addressConfirmed: productAccountDetails?.registration_address,
      notManual: productAccountDetails?.kyc_status?.kyc_status === 'COMPLETED',
    },
    documents: {
      verified: productAccountDetails?.document_status === 'Docs Approved',
      reKycVerified: productAccountDetails?.re_kyc_document_status === 'Docs Approved'
    },
    serviceAgreement: {
      generated: isReKYC ? false : AgreementDetails.status_value !== 'Not Generated',
      esigned: isReKYC ? false : AgreementDetails.status_value == 'Approved' || productAccountDetails?.is_service_agreement_esigned,
    },
    isReKYC,
    skippedStepsList,
    isChildAggregator,
    isPartnerAccount,
  });
};

// pan-details
const panStepProps = (productAccountDetails) => {
  const { serviceAgreement: { esigned }, isReKYC } = getProductAccountStatuses(productAccountDetails);
  return {
    completed: productAccountDetails && productAccountDetails.pan_verification_status === BANK_STATUS.VERIFIED,
    incomplete: !(productAccountDetails && productAccountDetails.pancard_number && productAccountDetails.business_entity_id),
    filled: productAccountDetails && !!productAccountDetails.pancard_name && !!productAccountDetails.pancard_number,
    editable: !esigned,
    locked: false,
  };
};

// business-detila
const businessDetailsStepProps = (productAccountDetails) => {
  const { serviceAgreement: { generated }, isReKYC } = getProductAccountStatuses(productAccountDetails);
  const completed =
    !!productAccountDetails &&
    !!productAccountDetails.business_category_id &&
    !!productAccountDetails.business_sub_category_id &&
    !!productAccountDetails.monthly_expected_volume;
 
    const reKYCCompleted = completed && productAccountDetails?.product_account_detail?.business_details_rekyced;

  return {
    completed: isReKYC ? reKYCCompleted : completed,
    incomplete: isReKYC ? !reKYCCompleted : !completed,
    editable: !generated,
    locked: !panStepProps(productAccountDetails).completed,
  };
};

// bank-details
const bankDetailsStepProps = (productAccountDetails) => {
  const bankDetailsFilled = !!productAccountDetails && !!productAccountDetails.bank_detail;
  const docUploaded = bankDetailsFilled && productAccountDetails.bank_detail.bank_verification_proof;
  const bankNotVerified =
    bankDetailsFilled && productAccountDetails.bank_detail.bank_verification_status !== BANK_STATUS.VERIFIED;
  const { serviceAgreement: { generated }, isReKYC } = getProductAccountStatuses(productAccountDetails);
  return {
    completed: bankDetailsFilled && productAccountDetails.bank_detail.bank_verification_status === BANK_STATUS.VERIFIED,
    filled: !!productAccountDetails && !!productAccountDetails.bank_detail,
    incomplete: !bankDetailsFilled || (bankDetailsFilled && !docUploaded && bankNotVerified),
    editable: !generated,
    error: bankDetailsFilled && productAccountDetails.bank_detail.bank_verification_status !== BANK_STATUS.VERIFIED,
    locked: !panStepProps(productAccountDetails).completed,
  };
};

// signing-authority
export const signingAuthorityStepProps = (productAccountDetails) => {
  const cinRequiredEntityTypes = ['Public Limited', 'Private Limited', 'LLP', 'One Person Company'];
  const signingAuthorityDetails = productAccountDetails?.signatory_contact_details?.[0];
  let completed = (
    signingAuthorityDetails?.name &&
    signingAuthorityDetails?.email &&
    signingAuthorityDetails?.pancard_number 
  );

  const { serviceAgreement: { generated }, documents: { verified: documentsApproved, reKycVerified: rekycDocumentsApproved  }, isReKYC } = getProductAccountStatuses(productAccountDetails);
  const entityType = productAccountDetails?.business_entity?.name;
  if (cinRequiredEntityTypes.includes(entityType)) { completed = completed && signingAuthorityDetails?.cin_number; }
  const reKYCCompleted = completed && signingAuthorityDetails?.rekyc_record;
  const attachedConfigs = productAccountDetails?.attached_configs || [];
  const skippedStepsList = getSkippedSteps({ attachedConfigs }) || [];
  const documentVerified = isReKYC ? rekycDocumentsApproved : documentsApproved;
  const ckycOrAadharCompleted = productAccountDetails ?. [isReKYC ? "re_kyc_kyc_status" : "kyc_status"]?.kyc_status === 'COMPLETED';
  return {
    completed: isReKYC ? reKYCCompleted : completed || skippedStepsList.indexOf(IDs.signingAuthority.default) >= 0,
    incomplete: isReKYC ? !reKYCCompleted : !completed,
    editable: !ckycOrAadharCompleted && !documentVerified && !generated,
    locked: !panStepProps(productAccountDetails).completed,
  };
};

// website-details
const websiteDetailsStepProps = (productAccountDetails) => {
  let website_approval_status = null;
  let completed = false;
  let incomplete = true;
  if (productAccountDetails) {
    if (productAccountDetails.integration_type === INTEGRATION_TYPE.NOT_SELECTED) {
      completed = false;
      incomplete = true;
    } else if (productAccountDetails.integration_type === INTEGRATION_TYPE.THIRD_PARTY) {
      if (productAccountDetails.website_detail) {
        ({ website_approval_status } = productAccountDetails.website_detail);
        if (website_approval_status == WEBSITE_APPROVAL_STATUS.PENDING) {
          completed = false;
          incomplete = false;
        } else if (website_approval_status == WEBSITE_APPROVAL_STATUS.IN_PROGRESS) {
          completed = false;
          incomplete = false;
        } else if (website_approval_status == WEBSITE_APPROVAL_STATUS.OK) {
          completed = true;
          incomplete = false;
        } else if (website_approval_status == WEBSITE_APPROVAL_STATUS.ERROR) {
          completed = false;
          incomplete = true;
        } else {
          completed = false;
          incomplete = true;
        }
      } else {
        completed = false;
        incomplete = true;
      }
    } else if (productAccountDetails.integration_type === INTEGRATION_TYPE.TOOLS) {
      completed = true;
      incomplete = false;
    } else {
      completed = false;
      incomplete = true;
    }
  } else {
    completed = false;
    incomplete = true;
  }

  const { serviceAgreement: { generated }, isReKYC } = getProductAccountStatuses(productAccountDetails);
  return {
    completed,
    incomplete,
    editable: !generated,
    locked: isReKYC || !panStepProps(productAccountDetails).completed,
  };
};

// kyc
const kycStepProps = (productAccountDetails) => {
  const addressConfirmed = productAccountDetails?.registration_address;
  const { serviceAgreement: { generated }, isReKYC } = getProductAccountStatuses(productAccountDetails);
  const document_status = isReKYC ? productAccountDetails?.re_kyc_document_status : productAccountDetails?.document_status;
  const documentsApproved = document_status == MERCHANT_DOC_STATUS.DOCUMENT_APPROVED;
  const ckycOrAadharCompleted = productAccountDetails ?. [isReKYC ? "re_kyc_kyc_status" : "kyc_status"]?.kyc_status === 'COMPLETED';
  return {
    completed: documentsApproved || (ckycOrAadharCompleted && addressConfirmed),
    incomplete: !(ckycOrAadharCompleted && addressConfirmed) && !documentsApproved && !productAccountDetails?.manualAddress,
    editable: !additionalDocumentsStepProps(productAccountDetails).completed && !generated,
    filled: documentsApproved || ckycOrAadharCompleted,
    locked: !signingAuthorityStepProps(productAccountDetails).completed,
  };
};

// additional documents
const additionalDocumentsStepProps = (productAccountDetails) => {
  const document_status = productAccountDetails?.re_kyc_required ? productAccountDetails?.re_kyc_document_status : productAccountDetails?.document_status;
  const { completed: esignCompleted } = serviceAgreementStepProps(productAccountDetails);
  const addressConfirmed = productAccountDetails?.registration_address;
  let incomplete = true;
  if (document_status == MERCHANT_DOC_STATUS.DOCUMENT_RECEIVED || document_status == MERCHANT_DOC_STATUS.DOCUMENT_APPROVED) {
    incomplete = false;
  }
  const { skippedStepsList, bankDetails, isChildAggregator, isPartnerAccount } = getProductAccountStatuses(productAccountDetails);
  const addressNotRequired = skippedStepsList.indexOf(IDs.kyc.default) >= 0;
  return {
    completed: document_status == MERCHANT_DOC_STATUS.DOCUMENT_APPROVED,
    incomplete: incomplete,
    editable: !esignCompleted,
    locked: !(panStepProps(productAccountDetails).completed && signingAuthorityStepProps(productAccountDetails).completed && (isChildAggregator || isPartnerAccount ? bankDetails?.filled : true)),
    hidden: addressNotRequired ? false : !addressConfirmed,
  };
};

// service agreement
const serviceAgreementStepProps = (productAccountDetails) => {
  const { serviceAgreement: { esigned }, isReKYC } = getProductAccountStatuses(productAccountDetails);
  // const {
  //   businessDetails: { completed: businessDetailsCompleted },
  //   website: { completed: websiteDetailsCompleted },
  //   documents: { verified: docsVerified },
  // } = getProductAccountStatuses(productAccountDetails);
  return {
    completed: esigned,
    incomplete: !esigned,
    // locked: !(businessDetailsCompleted && websiteDetailsCompleted && docsVerified),
    locked: isReKYC,
  };
};

//  Selectors
export const selectors = {
  getStepProps: (productAccountDetails, componentID) => {
    switch (componentID) {
      case IDs.panDetails.default:
        return panStepProps(productAccountDetails);
      case IDs.businessDetails.default:
        return businessDetailsStepProps(productAccountDetails);
      case IDs.bankDetails.default:
        return bankDetailsStepProps(productAccountDetails);
      case IDs.websiteDetails.default:
        return websiteDetailsStepProps(productAccountDetails);
      case IDs.signingAuthority.default:
        return signingAuthorityStepProps(productAccountDetails);
      case IDs.kyc.default:
        return kycStepProps(productAccountDetails);
      case IDs.additionalDocuments.default:
        return additionalDocumentsStepProps(productAccountDetails);
      case IDs.serviceAgreement.default:
        return serviceAgreementStepProps(productAccountDetails);
      default:
        return { completed: false, editable: true };
    }
  },
};
